import React, { Dispatch, FunctionComponent, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { Grid, Heading, Radio, Text, Input, useDate } from '@afterpaytouch/core'
import { OrderPaymentSchedule } from '@afterpaytouch/portal-api'
import { Money } from '../../../Money'
import { useRouter } from 'next/router'
import { PayNowInput, PayNowInputProps, PayNowModalContext } from '../../PayNowModalContext'
import { useTranslation, TFunction, Trans } from 'next-i18next'
import styles from './styles.module.scss'
import clsx from 'clsx'
import { isCustomAmountPayment } from '../../utils'
import {
  getInstallmentOwingFeeDisplayType,
  getSplitFeeAmounts,
  getTotalOwingFeeDisplayType,
  isScheduleDueToday,
  TotalOwingFeeDisplayType,
} from '../../../../state/orders/utils'
import { PaymentArrangementSchedule } from '@afterpaytouch/portal-api/consumer/hardship/types'
import { isHardshipScheduleDueToday, isHardshipScheduleOverdue, isPaymentArrangementSchedule, useGetTotalPayableHardshipRepayments } from '../../../../state'
import { Money as MoneyType, SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['payments']

interface LateFeesProps {
  isNextInstallmentOverdue: boolean
  nextInstallmentFees: MoneyType
  totalOwedFees: MoneyType
}

interface ScheduleProps {
  nextInstallment: OrderPaymentSchedule | PaymentArrangementSchedule
  remainingInstallments: MoneyType
  isLastInstallment: boolean
  totalInstallmentCount: number
  lateFeesInformation: LateFeesProps
  paymentScheduleSet?: OrderPaymentSchedule[]
}

interface InstallmentLabelBaseProps {
  heading: string
  text: string
  displayAmount: boolean
  amount?: MoneyType
  testNameSpace: string
  isDueToday?: boolean
  isOverdue?: boolean
  owedFeeAmount?: MoneyType
  owedFeeI18nKey?: string | null
  splitFailedPaymentFee?: MoneyType | null
  splitLateFee?: MoneyType | null
  isNextPayment?: boolean
  t?: TFunction
}

interface InstallmentItemsProps {
  schedules: ScheduleProps
  customAmount: {
    setCustomAmount: Dispatch<SetStateAction<string>>
    isCustomInputActive: boolean
    currencySymbol: string
    belowMaxAmount: boolean
  }
  handleChange: (event: PayNowInputProps) => void
  isLendingOrder: boolean
}

interface InstallmentProps {
  schedules: ScheduleProps
  isPaymentArrangement: boolean
  isLendingOrder: boolean
  t: TFunction
}

interface CustomInstallmentProps {
  displayCustomAmountInput: boolean
  currencySymbol: string
  handleChange: (_: any, value: string) => void
  belowMaxAmount: boolean
  customAmountState: string
  t: TFunction
}

export const InstallmentItems: FunctionComponent<InstallmentItemsProps> = ({ schedules, handleChange, customAmount, isLendingOrder }) => {
  const { payment, payNowStep } = useContext(PayNowModalContext)
  const [customAmountState, setCustomAmountState] = useState<string>(
    payNowStep !== PayNowInput.REMAINING && isCustomAmountPayment(payment) ? payment?.amount?.amount : ''
  )
  const { t } = useTranslation(I18N_NAMESPACE)
  const handleCustomChange = (_, value: string): void => {
    !isNaN(parseInt(value)) ? customAmount.setCustomAmount(value) : customAmount.setCustomAmount('0')
  }
  const isPaymentArrangement = isPaymentArrangementSchedule(schedules.nextInstallment)

  useEffect(() => {
    if (customAmountState !== '') {
      handleCustomChange(null, customAmountState)
    }
  }, [])

  return (
    <>
      <div className='mb-4 mt-6'>
        <Radio.Group
          value={payNowStep}
          onChange={(e) => {
            handleChange(e.target.value as PayNowInputProps)
          }}
          labelWidth='Full'
          labelPlacement='Top'
          kind='Option'
          name='paynow-selection-list'
          id='paynow-installments-radio-list'
        >
          {!schedules.isLastInstallment && (
            <Radio.Button
              value={PayNowInput.NEXT}
              testNameSpace='paynow-next'
              label={() => <NextInstallment schedules={schedules} t={t} isPaymentArrangement={isPaymentArrangement} isLendingOrder={isLendingOrder} />}
              onClick={() => {
                setCustomAmountState('')
              }}
            />
          )}
          <Radio.Button
            value={PayNowInput.REMAINING}
            testNameSpace='paynow-remaining'
            label={() => <RemainingInstallments schedules={schedules} t={t} isPaymentArrangement={isPaymentArrangement} isLendingOrder={isLendingOrder} />}
            onClick={() => {
              setCustomAmountState('')
            }}
          />
          <Radio.Button
            value={PayNowInput.CUSTOM}
            testNameSpace='paynow-custom'
            label={() => (
              <CustomInstallment
                displayCustomAmountInput={customAmount.isCustomInputActive}
                handleChange={handleCustomChange}
                currencySymbol={customAmount.currencySymbol}
                belowMaxAmount={customAmount.belowMaxAmount}
                customAmountState={customAmountState}
                t={t}
              />
            )}
          />
        </Radio.Group>
      </div>
    </>
  )
}

const getOwedFeeI18nKeyFromDisplayType = (displayType: TotalOwingFeeDisplayType): string | null => {
  switch (displayType) {
    case TotalOwingFeeDisplayType.LateFee:
      return 'payments:customPayment:installmentsv2:labelBase:incLateFee'
    case TotalOwingFeeDisplayType.FailedPaymentFee:
      return 'payments:customPayment:installmentsv2:labelBase:incFailedPaymentFee'
    case TotalOwingFeeDisplayType.FailedPaymentAndLateFee:
      return 'payments:customPayment:installmentsv2:labelBase:incFailedPaymentAndLateFee'
    default:
      return null
  }
}

const getInstallmentOwedFeeI18nKey = (
  feeAmount: MoneyType | null | undefined,
  orderFees: Array<{ amount: MoneyType; feeType?: string | null }> | undefined
): string | null => getOwedFeeI18nKeyFromDisplayType(getInstallmentOwingFeeDisplayType(feeAmount ?? undefined, orderFees))

const getTotalOwedFeeI18nKey = (paymentScheduleSet: OrderPaymentSchedule[] | undefined, feeAmount: MoneyType | null | undefined): string | null =>
  getOwedFeeI18nKeyFromDisplayType(getTotalOwingFeeDisplayType(paymentScheduleSet, feeAmount ?? undefined))

const InstallmentLabelBase: FunctionComponent<InstallmentLabelBaseProps> = ({
  heading,
  text,
  displayAmount,
  amount,
  testNameSpace,
  isOverdue = false,
  owedFeeAmount = null,
  owedFeeI18nKey = null,
  splitFailedPaymentFee = null,
  splitLateFee = null,
  isDueToday = false,
  isNextPayment = false,
  t = () => {},
}) => {
  return (
    <div data-testid={testNameSpace}>
      <Grid center>
        <Grid.Item sm={6} md={8}>
          <Heading renderAs='span' size='M' bold testNameSpace={testNameSpace}>
            {heading}
          </Heading>
          <Text renderAs='span' size='S' color={isNextPayment && isOverdue ? 'Fire' : 'Gray40'} testNameSpace={testNameSpace}>
            {text}
          </Text>
        </Grid.Item>
        {displayAmount && (
          <Grid.Item>
            <div className='mr-2 text-right'>
              {/* @ts-ignore: OPERATION BLEED STOPPER */}
              <Money size='M' value={amount} testNameSpace={testNameSpace} />
              {isOverdue && (
                <Text color='Fire' testNameSpace='installment-payment-overdue'>
                  {t<string>('payments:customPayment:installmentsv2:labelBase:overdue')}
                </Text>
              )}
              {isDueToday && (
                <Text color='Gray40' testNameSpace='installment-payment-due-today'>
                  {t<string>('payments:customPayment:installmentsv2:labelBase:today')}
                </Text>
              )}
            </div>
          </Grid.Item>
        )}
      </Grid>
      {owedFeeI18nKey != null && (
        <div className='mr-2 text-right'>
          <Text testNameSpace={`owed-fee-${testNameSpace}`}>
            <Trans
              i18nKey={owedFeeI18nKey}
              components={{
                // @ts-expect-error: value may be null when fee type not present; Trans only interpolates when key references it
                lateFeeAmount: <Money size='S' value={splitLateFee ?? owedFeeAmount} testNameSpace={`owed-fee-${testNameSpace}`} />,
                // @ts-expect-error: value may be null when fee type not present; Trans only interpolates when key references it
                failedPaymentAmount: <Money size='S' value={splitFailedPaymentFee ?? owedFeeAmount} testNameSpace={`owed-fee-fp-${testNameSpace}`} />,
              }}
            />
          </Text>
        </div>
      )}
    </div>
  )
}

export const NextInstallment: FunctionComponent<InstallmentProps> = ({ schedules, t, isPaymentArrangement, isLendingOrder }) => {
  const router = useRouter()
  const { formatDate, formatUnixDate } = useDate({
    locale: router?.locale as SupportedLocale,
    year: 'numeric',
  })
  const totalNumberOfHardshipPayments = useGetTotalPayableHardshipRepayments()
  const context = isLendingOrder ? 'PCL' : ''

  if (isPaymentArrangement) {
    const { nextInstallment } = schedules ?? {}
    const { overdueDatetime, outstandingBalance } = (nextInstallment as PaymentArrangementSchedule) ?? {}
    const dueDate = formatUnixDate(overdueDatetime)

    return InstallmentLabelBase({
      heading: t('payments:customPayment:installmentsv2:base:headings:next'),
      text: `1 ${t('payments:customPayment:installmentsv2:base:subheading:next')} ${totalNumberOfHardshipPayments} • ${dueDate}`,
      displayAmount: true,
      amount: outstandingBalance,
      testNameSpace: 'next-payment-option-hardship',
      isOverdue: isHardshipScheduleOverdue(schedules.nextInstallment as PaymentArrangementSchedule),
      isDueToday: isHardshipScheduleDueToday(dueDate),
      isNextPayment: true,
      t,
    })
  } else {
    const { nextInstallment, totalInstallmentCount, lateFeesInformation, isLastInstallment, paymentScheduleSet } = schedules ?? {}
    const { installmentDueDate, amountPayable, installmentSequence, orderFees } = (nextInstallment as OrderPaymentSchedule) ?? {}
    const { isNextInstallmentOverdue, nextInstallmentFees } = lateFeesInformation ?? {}
    const formattedInstallmentDate = formatDate(installmentDueDate)
    const splitFees = getSplitFeeAmounts(orderFees)

    return InstallmentLabelBase({
      heading: isLastInstallment
        ? t('payments:customPayment:installmentsv2:base:headings:total')
        : t('payments:customPayment:installmentsv2:base:headings:next', { context }),
      text: isLendingOrder
        ? t('payments:customPayment:installmentsv2:base:subheading:next', { date: formattedInstallmentDate, context })
        : `${installmentSequence} ${t('payments:customPayment:installmentsv2:base:subheading:next')} ${totalInstallmentCount} • ${formattedInstallmentDate}`,
      displayAmount: true,
      amount: amountPayable,
      testNameSpace: 'next-payment-option',
      isOverdue: isNextInstallmentOverdue,
      owedFeeAmount: nextInstallmentFees,
      owedFeeI18nKey: getInstallmentOwedFeeI18nKey(nextInstallmentFees, orderFees),
      splitFailedPaymentFee: splitFees.failedPaymentFee,
      splitLateFee: splitFees.lateFee,
      isDueToday: isScheduleDueToday(nextInstallment as OrderPaymentSchedule),
      isNextPayment: true,
      t,
    })
  }
}

export const RemainingInstallments: FunctionComponent<InstallmentProps> = ({ schedules, t, isPaymentArrangement, isLendingOrder }) => {
  const router = useRouter()
  const { remainingInstallments, lateFeesInformation, nextInstallment, paymentScheduleSet } = schedules ?? {}
  const { isNextInstallmentOverdue, totalOwedFees } = lateFeesInformation ?? {}
  const { formatUnixDate } = useDate({
    locale: router?.locale as SupportedLocale,
    year: 'numeric',
  })
  const isInstallmentDueToday = useCallback((): boolean => {
    if (isPaymentArrangement) {
      const { overdueDatetime } = (nextInstallment as PaymentArrangementSchedule) ?? {}
      return isHardshipScheduleDueToday(formatUnixDate(overdueDatetime))
    } else {
      return isScheduleDueToday(nextInstallment as OrderPaymentSchedule)
    }
  }, [isPaymentArrangement, nextInstallment, formatUnixDate])
  const context = isLendingOrder ? 'PCL' : ''
  const allOrderFees = (paymentScheduleSet ?? []).flatMap((schedule) => (schedule.orderFees ?? []) as Array<{ amount: MoneyType; feeType?: string | null }>)
  const totalSplitFees = getSplitFeeAmounts(allOrderFees)

  return InstallmentLabelBase({
    heading: t('payments:customPayment:installmentsv2:base:headings:total', { context }),
    text: isPaymentArrangement
      ? t('payments:customPayment:installmentsv2:base:subheadings:totalHardship')
      : t('payments:customPayment:installmentsv2:base:subheadings:total', { context }),
    displayAmount: true,
    amount: remainingInstallments,
    testNameSpace: 'remaining-payment-option',
    isOverdue: isNextInstallmentOverdue,
    owedFeeAmount: totalOwedFees,
    owedFeeI18nKey: getTotalOwedFeeI18nKey(paymentScheduleSet, totalOwedFees),
    splitFailedPaymentFee: totalSplitFees.failedPaymentFee,
    splitLateFee: totalSplitFees.lateFee,
    isDueToday: schedules.isLastInstallment && isInstallmentDueToday(),
    t,
  })
}

export const CustomInstallment: FunctionComponent<CustomInstallmentProps> = ({
  displayCustomAmountInput,
  handleChange,
  currencySymbol,
  belowMaxAmount,
  customAmountState,
  t,
}) => {
  return (
    <>
      {InstallmentLabelBase({
        heading: t('payments:customPayment:installmentsv2:base:headings:custom'),
        text: t('payments:customPayment:installmentsv2:base:subheadings:custom'),
        displayAmount: false,
        testNameSpace: 'custom-payment-option',
      })}
      <div className={clsx(displayCustomAmountInput ? styles.customInputDiv : styles.customInputDivClose)}>
        {displayCustomAmountInput && (
          <Grid>
            <Grid.Item>
              <Input.Tel
                label={t('payments:customPayment:installmentsv2:base:subheadings:custom:input')}
                format={`${currencySymbol ?? '$'}[000{0.00}]`}
                fullWidth
                onChange={handleChange}
                error={!belowMaxAmount}
                autoFocus={displayCustomAmountInput}
                value={customAmountState}
                testNameSpace='paynow-custom-amount'
                inputMode='decimal'
              />
            </Grid.Item>
          </Grid>
        )}
      </div>
    </>
  )
}
